import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { RequirementDto } from 'src/app/compliance/api/models/requirement-dto.model';
import { ComplianceDocumentTypeEnum } from 'src/app/compliance/enums/compliance-document-type.enum';
import { ComplianceReportDocumentType } from 'src/app/compliance/models/compliance-report-models';
import { Document } from 'src/app/compliance/models/document';
import { WINDOW } from 'src/app/core/core.module';
import { DocumentService } from 'src/app/core/document.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { ComplianceReportClient } from '../../compliance/api/compliance-report-client.service';
import { WinningRuleSetClient } from '../../compliance/api/winning-rule-set-client.service';
import { ContractRepipeService } from './contract-repipe.service';
import { DocumentForComplianceReportDto } from 'src/app/compliance/api/models/document-for-compliance-report-dto.model';
import { forkJoin, of } from 'rxjs';
import { take, switchMap, catchError } from 'rxjs/operators';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { CfdMatchResult } from 'src/app/compliance/api/models/cfd-match-result.model';

@Injectable({
    providedIn: 'root'
})
export class RequirementDetailsViewerService {
    constructor(
        private readonly _documentService: DocumentService,
        private readonly _contractRepipeService: ContractRepipeService,
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _store: Store,
        private readonly complianceReportClient: ComplianceReportClient,
        private readonly _winningRuleSetClient: WinningRuleSetClient,
        private readonly _featureManager: LDFeatureManager
    ) {}

    showTextRequirements(
        docType: ComplianceReportDocumentType,
        textRequirementsWindowPosition: { top: number; left: number },
        textRequirementsWindow: Window | null,
        contractId: number
    ): void {
        if (!docType.hasNonGlobalDescription && !docType.hasGlobalOnlyOrExternalDescription) {
            return;
        }
        const viewerUrl = '/assets/requirement-details.html';

        if (textRequirementsWindow) {
            if (textRequirementsWindow.screenTop) {
                textRequirementsWindowPosition.top = textRequirementsWindow.screenTop;
            }

            if (textRequirementsWindow.screenLeft) {
                textRequirementsWindowPosition.left = textRequirementsWindow.screenLeft;
            }
            textRequirementsWindow.close();
        }

        textRequirementsWindow = this._window.open(
            viewerUrl,
            'text-reqs',
            `height=${this._window.outerHeight * 0.4},width=${this._window.outerWidth * 0.25},top=${
                textRequirementsWindowPosition.top
            },left=${textRequirementsWindowPosition.left}`
        );

        if (textRequirementsWindow) {
            textRequirementsWindow.onload = () => {
                textRequirementsWindow.onbeforeunload = ($event) => {
                    const window: Window = $event.currentTarget as Window;
                    if (window) {
                        textRequirementsWindowPosition.top = window.screenTop;
                        textRequirementsWindowPosition.left = window.screenLeft;
                    }
                };
            };

            const reqs$ = this.complianceReportClient.getComplianceReportDocumentRequirementDescription(
                docType.id,
                contractId
            );
            const cfdResult$ = this._featureManager.isEnabled(FeatureFlag.ShowCRUpdatedRuleBuilderCFD).pipe(
                take(1),
                switchMap((enabled) =>
                    enabled
                        ? this._winningRuleSetClient.getDescription({
                              contractId,
                              documentTypeId: docType.id,
                              forExternal: false,
                              renderDemographicFilters: false,
                              demographicFilters: null
                          })
                        : of(null as CfdMatchResult | null)
                ),
                catchError(() => of(null as CfdMatchResult | null))
            );
            const combined$ = forkJoin({ reqs: reqs$, cfdResult: cfdResult$ });
            (textRequirementsWindow as any).data = { combined$, reqName: docType.name };
        }
    }

    showFile(
        docType: ComplianceReportDocumentType,
        doc: Document,
        contractId: number,
        windowDetails: { top: number; left: number },
        pipeDetails: { isContractRepiped: boolean },
        changeWindow: (child: Window) => void
    ): void {
        const openDocument = (doc: Document) => {
            this._documentService
                .openDocument(doc.id, doc.id.toString(), windowDetails.top, windowDetails.left)
                .subscribe((child) => this.handleDocumentOpen(contractId, child, pipeDetails, docType, changeWindow));
        };

        const openContractRepipe = (doc: Document) => {
            this._contractRepipeService
                .openDocumentRepipe(
                    contractId,
                    doc.id,
                    docType.id,
                    doc.id.toString(),
                    windowDetails.top,
                    windowDetails.left
                )
                .subscribe((child) =>
                    this.handleDocumentOpen(contractId, child, pipeDetails, docType, changeWindow, true)
                );
        };

        if (docType.id === ComplianceDocumentTypeEnum.SignedContract && pipeDetails.isContractRepiped) {
            openContractRepipe(doc);
        } else {
            openDocument(doc);
        }
    }

    showFileNew(
        docType: RequirementDto,
        doc: DocumentForComplianceReportDto,
        contractId: number,
        windowDetails: { top: number; left: number },
        pipeDetails: { isContractRepiped: boolean },
        changeWindow: (child: Window) => void
    ): void {
        const openDocument = (doc: DocumentForComplianceReportDto) => {
            this._documentService
                .openDocument(doc.documentId, doc.documentId.toString(), windowDetails.top, windowDetails.left)
                .subscribe((child) =>
                    this.handleDocumentOpenNew(contractId, child, pipeDetails, docType, changeWindow)
                );
        };

        const openContractRepipe = (doc: DocumentForComplianceReportDto) => {
            this._contractRepipeService
                .openDocumentRepipe(
                    contractId,
                    doc.documentId,
                    docType.documentTypeId,
                    doc.documentId.toString(),
                    windowDetails.top,
                    windowDetails.left
                )
                .subscribe((child) =>
                    this.handleDocumentOpenNew(contractId, child, pipeDetails, docType, changeWindow, true)
                );
        };

        if (docType.documentTypeId === ComplianceDocumentTypeEnum.SignedContract && pipeDetails.isContractRepiped) {
            openContractRepipe(doc);
        } else {
            openDocument(doc);
        }
    }

    private handleDocumentOpenNew(
        contractId: number,
        child: any,
        pipeDetails: { isContractRepiped: boolean },
        docType: RequirementDto,
        changeWindow: (child: Window) => void,
        isContract = false
    ): void {
        if (child) {
            changeWindow(child);

            if (docType) {
                const reqs = this.complianceReportClient.getComplianceReportDocumentRequirementDescription(
                    docType.documentTypeId,
                    contractId
                );

                child.data = {
                    reqs
                };
            }
        } else {
            pipeDetails.isContractRepiped = !isContract;

            this._store.dispatch(
                showToasterMessage({ message: 'Failed to open document! Check the browser popup settings.' })
            );
        }
    }

    private handleDocumentOpen(
        contractId: number,
        child: any,
        pipeDetails: { isContractRepiped: boolean },
        docType: ComplianceReportDocumentType,
        changeWindow: (child: Window) => void,
        isContract = false
    ): void {
        if (child) {
            changeWindow(child);

            if (docType.hasRequirements) {
                const reqs = this.complianceReportClient.getComplianceReportDocumentRequirementDescription(
                    docType.id,
                    contractId
                );

                child.data = {
                    reqs
                };
            }
        } else {
            pipeDetails.isContractRepiped = !isContract;

            this._store.dispatch(
                showToasterMessage({ message: 'Failed to open document! Check the browser popup settings.' })
            );
        }
    }
}
